import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Download, CopyPlus, Pencil, Trash2, IndianRupee, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import SendEmailDialog from "@/components/app/SendEmailDialog";
import BulkPayslipEmailDialog from "@/components/app/BulkPayslipEmailDialog";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const daysInMonth = (period) => {
  if (!period || !period.includes("-")) return 30;
  const [y, m] = period.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};

const compute = (r) => {
  const s = Number(r.salary) || 0,
    e = Number(r.esi) || 0,
    pf = Number(r.pf) || 0,
    pfEmp = Number(r.pf_employee) || 0,
    t = Number(r.pt) || 0,
    b = Number(r.bonus) || 0,
    inc = Number(r.incentive) || 0,
    tds = Number(r.tds) || 0,
    d = Number(r.deduction) || 0,
    ar = Number(r.advance_recovery) || 0,
    lopDays = Number(r.lop_days) || 0;
  const dim = daysInMonth(r.period_month);
  const perDay = dim ? s / dim : 0;
  const lopDeduction = Math.round(perDay * lopDays * 100) / 100;
  // `deduction` is the MANUAL value, `advance_recovery` piles on top automatically.
  const total_ded = d + ar;
  return {
    gross: s + e + pf + b + inc - total_ded,
    net: s + b + inc - total_ded - t - pfEmp - tds - lopDeduction,
    total_ded,
    lopDeduction,
    perDay: Math.round(perDay * 100) / 100,
  };
};

const emptyForm = {
  user_id: "",
  user_name: "",
  period_month: currentMonth(),
  salary: 0,
  esi: 0,
  pf: 0,
  pf_employee: 0,
  pt: 0,
  bonus: 0,
  incentive: 0,
  tds: 0,
  lop_days: 0,
  deduction: 0,
  advance_recovery: 0,
  note: "",
};

export default function Payroll() {
  const { user, hasPermission } = useAuth();
  const { selectedId } = useProperty();
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailFor, setEmailFor] = useState(null); // {user, payslip}
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);

  const propertyParam = selectedId ? `&property_id=${selectedId}` : "";

  const load = useCallback(async () => {
    try {
      const [p, r, s] = await Promise.all([
        api.get(`/payslips?period_month=${month}${propertyParam}`),
        api.get(`/payroll/report?period_month=${month}${propertyParam}`),
        api.get(`/users${selectedId ? `?property_id=${selectedId}` : ""}`).catch(() => ({ data: [] })),
      ]);
      setRows(p.data);
      setReport(r.data);
      setStaff(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payroll");
    }
  }, [month, selectedId, propertyParam]);

  useEffect(() => { load(); }, [load]);

  const rowsByUser = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.user_id] = r;
    return m;
  }, [rows]);

  const openCreateFor = async (u) => {
    const existing = rowsByUser[u.id];
    if (existing) {
      setForm({
        ...existing,
        user_name: u.name,
        deduction: Number(existing.manual_deduction ?? existing.deduction ?? 0),
        advance_recovery: Number(existing.advance_recovery ?? 0),
      });
      setOpen(true);
      return;
    }
    // No payslip yet — try auto-prefilling from the active Salary Structure
    let prefill = { ...emptyForm, user_id: u.id, user_name: u.name, period_month: month };
    try {
      // effective_on = last day of the selected period so a structure dated
      // mid-month is correctly picked up for that month's payroll.
      const [yr, mo] = month.split("-").map(Number);
      const lastDay = new Date(yr, mo, 0).getDate();
      const effectiveOn = `${month}-${String(lastDay).padStart(2, "0")}`;
      const { data: ss } = await api.get(
        `/salary-structures/active?user_id=${u.id}&effective_on=${effectiveOn}`
      );
      if (ss) {
        const hraVal = ss.hra_applicable !== false ? Number(ss.hra || 0) : 0;
        prefill = {
          ...prefill,
          salary: Number(ss.basic || 0) + hraVal + Number(ss.special_allowance || 0),
          esi: Number(ss.esi || 0),
          pf: Number(ss.pf_employer || 0),
          pf_employee: Number(ss.pf_employee || 0),
          pt: Number(ss.pt || 0),
          note: ss.effective_from ? `Auto-filled from salary structure (eff. ${ss.effective_from})` : "",
        };
        toast.success("Auto-filled from active salary structure");
      }
    } catch (e) {
      // No active salary structure for this employee yet — that's fine,
      // admin will enter values manually. Don't spam toast for the
      // common case of 404, just log for diagnostics.
      if (e?.response?.status && e.response.status !== 404) {
        logger.warn("Salary structure prefill failed", e);
      }
    }
    setForm(prefill);
    setOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      ...r,
      deduction: Number(r.manual_deduction ?? r.deduction ?? 0),
      advance_recovery: Number(r.advance_recovery ?? 0),
    });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        user_id: form.user_id,
        period_month: form.period_month,
        salary: Number(form.salary) || 0,
        esi: Number(form.esi) || 0,
        pf: Number(form.pf) || 0,
        pf_employee: Number(form.pf_employee) || 0,
        pt: Number(form.pt) || 0,
        bonus: Number(form.bonus) || 0,
        incentive: Number(form.incentive) || 0,
        tds: Number(form.tds) || 0,
        lop_days: Number(form.lop_days) || 0,
        deduction: Number(form.deduction) || 0,
        note: form.note || null,
      };
      await api.post("/payslips", payload);
      toast.success("Payslip saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.period_month} payslip for ${r.user_name}?`)) return;
    try {
      await api.delete(`/payslips/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copyPrev = async () => {
    if (!window.confirm(`Copy previous month's salaries into ${month}? Existing payslips will be kept as-is.`)) return;
    try {
      const { data } = await api.post(`/payslips/copy-previous?period_month=${month}${propertyParam}`);
      toast.success(`Copied ${data.created} payslip(s) · skipped ${data.skipped}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const exportCSV = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/payslips/export?period_month=${month}${propertyParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslips_${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const downloadPdf = async (r) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/payslips/${r.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip_${(r.user_name || "employee").replace(/\s+/g, "_")}_${r.period_month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const totals = report?.totals || {};
  const liveCompute = compute(form);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Compensation</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Payroll</h1>
          <p className="text-sm text-zinc-500 mt-2">Enter monthly salaries, contributions, and one-time adjustments. CTC = Salary + ESI + Employer PF + Bonus + Incentive − Deduction. Net = Salary + Bonus + Incentive − Deduction − PT − Employee PF − TDS − LOP. LOP days are auto-counted from approved leaves; admin can override.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label>Period</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="payroll-month" />
          </div>
          <Button variant="outline" onClick={copyPrev} data-testid="payroll-copy-prev"><CopyPlus className="w-4 h-4 mr-1" /> Copy previous</Button>
          <Button variant="outline" onClick={exportCSV} data-testid="payroll-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          {hasPermission && hasPermission("email_send") && (
            <Button
              variant="outline"
              onClick={() => setBulkEmailOpen(true)}
              disabled={rows.length === 0}
              title={rows.length === 0 ? "No payslips for this month yet" : "Email this month's payslips to selected employees"}
              data-testid="payroll-bulk-email"
            >
              <Send className="w-4 h-4 mr-1" /> Email payslips
            </Button>
          )}
        </div>
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
        <SumCard label="Employees" value={report?.employee_count ?? 0} icon={Banknote} />
        <SumCard label="Salary" value={fmtINR(totals.salary)} />
        <SumCard label="ESI + PF + PT" value={fmtINR((totals.esi || 0) + (totals.pf || 0) + (totals.pt || 0))} />
        <SumCard label="Gross" value={fmtINR(totals.gross)} accent="amber" />
        <SumCard label="Net Payable" value={fmtINR(totals.net_payable)} accent="green" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-3 px-3">Employee</th>
              <th className="py-3 px-3 text-right">Salary</th>
              <th className="py-3 px-3 text-right">ESI</th>
              <th className="py-3 px-3 text-right">PF</th>
              <th className="py-3 px-3 text-right">PT</th>
              <th className="py-3 px-3 text-right">Bonus</th>
              <th className="py-3 px-3 text-right">Deduction</th>
              <th className="py-3 px-3 text-right">Gross</th>
              <th className="py-3 px-3 text-right">Net</th>
              <th className="py-3 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((u) => {
              const r = rowsByUser[u.id];
              return (
                <tr key={u.id} className="border-b border-zinc-100" data-testid={`payroll-row-${u.id}`}>
                  <td className="py-2.5 px-3">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{u.employee_id || "—"} · {u.role.replace("_", " ")}</div>
                  </td>
                  {r ? (
                    <>
                      <td className="py-2.5 px-3 text-right font-mono">{fmtINR(r.salary)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtINR(r.esi)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtINR(r.pf)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtINR(r.pt)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-emerald-700">{r.bonus ? `+${fmtINR(r.bonus)}` : "—"}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-red-600">
                        {r.deduction ? `−${fmtINR(r.deduction)}` : "—"}
                        {r.advance_recovery > 0 && (
                          <div className="text-[10px] text-zinc-500 font-normal mt-0.5" title="Auto-recovered approved advances">
                            incl. ₹{Number(r.advance_recovery).toFixed(0)} adv.
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-semibold">{fmtINR(r.gross)}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-700">{fmtINR(r.net_payable)}</td>
                      <td className="py-2.5 px-3 text-right">
                        <button onClick={() => downloadPdf(r)} className="p-1.5 hover:bg-zinc-100 rounded" title="Download PDF" data-testid={`download-payslip-${u.id}`}>
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEmailFor({ user: u, payslip: r })} className="p-1.5 hover:bg-zinc-100 rounded ml-1" title="Email this payslip" data-testid={`email-payslip-${u.id}`}>
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-zinc-100 rounded ml-1" data-testid={`edit-payslip-${u.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(r)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded ml-1" data-testid={`delete-payslip-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td colSpan={8} className="py-2.5 px-3 text-zinc-400 text-xs">No payslip for {month}</td>
                      <td className="py-2.5 px-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => openCreateFor(u)} data-testid={`add-payslip-${u.id}`}>
                          <IndianRupee className="w-3.5 h-3.5 mr-1" /> Add
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {staff.length === 0 && (
              <tr><td colSpan={10} className="py-10 text-center text-zinc-500">No staff in scope.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="payslip-dialog">
          <DialogHeader>
            <DialogTitle>Payslip · {form.user_name}</DialogTitle>
            <DialogDescription>Enter monthly amounts. Gross and Net are auto-computed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Period (YYYY-MM)</Label>
              <Input type="month" value={form.period_month} onChange={(e) => setForm({ ...form, period_month: e.target.value })} data-testid="ps-period" />
            </div>
            <div>
              <Label>Salary <span className="text-red-500">*</span></Label>
              <Input type="number" min="0" step="0.01" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} data-testid="ps-salary" />
            </div>
            <div>
              <Label>ESI</Label>
              <Input type="number" min="0" step="0.01" value={form.esi} onChange={(e) => setForm({ ...form, esi: e.target.value })} data-testid="ps-esi" />
            </div>
            <div>
              <Label>Employer PF</Label>
              <Input type="number" min="0" step="0.01" value={form.pf} onChange={(e) => setForm({ ...form, pf: e.target.value })} data-testid="ps-pf" />
              <p className="text-[10px] text-zinc-500 mt-0.5">Cost-to-company side</p>
            </div>
            <div>
              <Label>Employee PF</Label>
              <Input type="number" min="0" step="0.01" value={form.pf_employee} onChange={(e) => setForm({ ...form, pf_employee: e.target.value })} data-testid="ps-pf-employee" />
              <p className="text-[10px] text-zinc-500 mt-0.5">Deducted from take-home</p>
            </div>
            <div>
              <Label>PT</Label>
              <Input type="number" min="0" step="0.01" value={form.pt} onChange={(e) => setForm({ ...form, pt: e.target.value })} data-testid="ps-pt" />
            </div>
            <div>
              <Label>Bonus</Label>
              <Input type="number" min="0" step="0.01" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} data-testid="ps-bonus" />
            </div>
            <div>
              <Label>Incentive</Label>
              <Input type="number" min="0" step="0.01" value={form.incentive} onChange={(e) => setForm({ ...form, incentive: e.target.value })} data-testid="ps-incentive" />
            </div>
            <div>
              <Label>TDS</Label>
              <Input type="number" min="0" step="0.01" value={form.tds} onChange={(e) => setForm({ ...form, tds: e.target.value })} data-testid="ps-tds" />
              <p className="text-[10px] text-zinc-500 mt-0.5">Income tax deducted at source</p>
            </div>
            <div>
              <Label>LOP days</Label>
              <Input type="number" min="0" step="0.5" value={form.lop_days} onChange={(e) => setForm({ ...form, lop_days: e.target.value })} data-testid="ps-lop-days" />
              <p className="text-[10px] text-zinc-500 mt-0.5">
                Auto-deducts {fmtINR(liveCompute.lopDeduction)} (₹{liveCompute.perDay}/day × {form.lop_days || 0} days)
              </p>
            </div>
            <div className="col-span-2">
              <Label>One-time deduction</Label>
              <Input type="number" min="0" step="0.01" value={form.deduction} onChange={(e) => setForm({ ...form, deduction: e.target.value })} data-testid="ps-deduction" />
              <p className="text-[11px] text-zinc-500 mt-1">Manual adjustments only. Approved advances are recovered automatically below.</p>
            </div>
            <div className="col-span-2">
              <Label>Advance recovery (auto)</Label>
              <Input type="number" value={form.advance_recovery || 0} disabled className="bg-zinc-50" data-testid="ps-advance-recovery" />
              <p className="text-[11px] text-zinc-500 mt-1">
                Sum of approved advances pulled into this payslip. Reverses if the payslip is deleted.
              </p>
            </div>
            <div className="col-span-2">
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. festival bonus, advance recovery" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-zinc-200 pt-3">
            <div>
              <div className="label-overline">Gross (employer cost)</div>
              <div className="text-lg font-bold font-mono" data-testid="ps-gross">{fmtINR(liveCompute.gross)}</div>
            </div>
            <div>
              <div className="label-overline">Net payable</div>
              <div className="text-lg font-bold text-emerald-700 font-mono" data-testid="ps-net">{fmtINR(liveCompute.net)}</div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy || !form.user_id} className="bg-zinc-950 hover:bg-zinc-800" data-testid="ps-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={!!emailFor}
        onOpenChange={(v) => !v && setEmailFor(null)}
        title={`Email payslip — ${emailFor?.user?.name || ""}`}
        defaultCategory="payslip"
        toUser={emailFor?.user ? { id: emailFor.user.id, name: emailFor.user.name, email: emailFor.user.email } : null}
        payslipId={emailFor?.payslip?.id}
        extraPlaceholders={emailFor?.payslip ? {
          period_month: emailFor.payslip.period_month,
          period_label: emailFor.payslip.period_month,
          net_payable: Math.round(Number(emailFor.payslip.net_payable || 0)),
        } : {}}
      />

      <BulkPayslipEmailDialog
        open={bulkEmailOpen}
        onOpenChange={setBulkEmailOpen}
        period_month={month}
        propertyId={selectedId || null}
        rows={rows}
        staff={staff}
      />
    </div>
  );
}

function SumCard({ label, value, accent }) {
  const palette = {
    green: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
  };
  return (
    <div className={`bg-white border border-zinc-200 rounded-md p-4 ${accent ? palette[accent] : ""}`}>
      <div className="label-overline">{label}</div>
      <div className="text-2xl font-bold font-display mt-1">{value}</div>
    </div>
  );
}
